import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FolderKanban,
  Plus,
  Users,
  Calendar as CalendarIcon,
  LayoutGrid,
  List as ListIcon,
  BarChart2,
  Clock,
  CheckCircle2,
  Settings,
  Star,
  RefreshCw,
  Search,
  CircleDot,
  Flag,
  X,
} from 'lucide-react';
import { MainLayout } from '../../components/MainLayout';
import api from '../../api/client';
import TaskCreateModal from '../../components/tasks/TaskCreateModal';
import TaskDetailDrawer from '../../components/tasks/TaskDetailDrawer';
import TaskKanbanBoard from '../../components/tasks/TaskKanbanBoard';
import TaskCalendarView from '../../components/tasks/TaskCalendarView';
import TaskTimelineView from '../../components/tasks/TaskTimelineView';
import ProjectMembersDrawer from '../../components/tasks/ProjectMembersDrawer';

export default function ProjectDetailApp() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list'); // list, board, calendar, timeline

  // Drawers & Modals
  const [membersDrawerOpen, setMembersDrawerOpen] = useState(false);
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false);
  const [detailDrawerTaskId, setDetailDrawerTaskId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const fetchProjectData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [projRes, tasksRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get('/tasks', { params: { projectId } }),
      ]);
      setProject(projRes.data);
      setTasks(tasksRes.data || []);
    } catch (err) {
      console.error('Failed to load project details:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  const handleStatusChange = async (taskId, nextStatus) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: nextStatus });
      fetchProjectData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update task status');
    }
  };

  const filteredTasks = tasks.filter((t) => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (priorityFilter !== 'all' && t.priority !== priorityFilter) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return t.title.toLowerCase().includes(q) || (t.description && t.description.toLowerCase().includes(q));
  });

  if (loading && !project) {
    return (
      <MainLayout>
        <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          Loading project workspace...
        </div>
      </MainLayout>
    );
  }

  if (!project) {
    return (
      <MainLayout>
        <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          Project not found or access denied.
          <div style={{ marginTop: '16px' }}>
            <button onClick={() => navigate('/apps/tasks/projects')} className="btn-primary">
              Back to Projects
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Top Header - Full Bleed Edge-to-Edge */}
      <div className="projects-hero-header notes-hero-banner">
        <div className="notes-hero-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <button
              onClick={() => navigate('/apps/tasks/projects')}
              className="btn-secondary notes-hero-btn-secondary"
              style={{ padding: '8px', borderRadius: '8px', display: 'flex' }}
              title="Back to Projects"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h1 className="notes-hero-title">
                  {project.name}
                </h1>
                <span className="notes-hero-badge" style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px' }}>
                  {project.status}
                </span>
              </div>
              {project.description && (
                <p className="notes-hero-subtitle">
                  {project.description}
                </p>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={() => setMembersDrawerOpen(true)}
              className="btn-secondary notes-hero-btn-secondary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <Users size={16} />
              <span>Members ({project.members?.length || 1})</span>
            </button>

            <button
              onClick={() => setCreateTaskModalOpen(true)}
              className="btn-primary notes-hero-btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <Plus size={16} />
              <span>Add Task</span>
            </button>
          </div>
        </div>
      </div>

      <div className="project-detail-content-container" style={{ maxWidth: '1600px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* Project Header Stats Bar */}
        <div className="project-detail-stats-bar">
          {/* Progress metric */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: '200px', flex: 1 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                <span>Overall Progress</span>
                <strong style={{ color: 'var(--text-primary)' }}>{project.progress}%</strong>
              </div>
              <div style={{ height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${project.progress}%`, background: 'var(--accent-primary)' }} />
              </div>
            </div>
          </div>

          {/* Task stats pills */}
          <div className="project-stats-pills" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              Total: <strong style={{ color: 'var(--text-primary)' }}>{project.task_stats?.total || 0}</strong>
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              Completed: <strong style={{ color: 'var(--success)' }}>{project.task_stats?.completed || 0}</strong>
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              In Progress: <strong style={{ color: 'var(--accent-primary)' }}>{project.task_stats?.in_progress || 0}</strong>
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              Blocked: <strong style={{ color: 'var(--danger)' }}>{project.task_stats?.blocked || 0}</strong>
            </div>
          </div>
        </div>

        {/* Workspace View Mode Switcher */}
        <div className="project-view-switcher-bar">
          <div className="project-tabs-row">
            {[
              { id: 'list', label: 'List', icon: ListIcon },
              { id: 'board', label: 'Board', icon: LayoutGrid },
              { id: 'calendar', label: 'Calendar', icon: CalendarIcon },
              { id: 'timeline', label: 'Timeline', icon: BarChart2 },
            ].map((v) => {
              const Icon = v.icon;
              return (
                <button
                  key={v.id}
                  onClick={() => setActiveTab(v.id)}
                  className={`project-view-tab-btn ${activeTab === v.id ? 'btn-primary' : 'btn-secondary'}`}
                >
                  <Icon size={15} />
                  <span>{v.label}</span>
                </button>
              );
            })}
          </div>

          {/* Desktop Filter Row (> 640px) */}
          <div className="project-desktop-filters-row project-filters-row">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '0 12px',
                height: '38px',
                flex: 1,
                minWidth: '180px',
              }}
            >
              <Search size={15} color="var(--text-secondary)" style={{ flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search in project..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-primary)',
                  outline: 'none',
                  fontSize: '13px',
                  width: '100%',
                }}
              />
            </div>

            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="all">All Statuses</option>
              <option value="BACKLOG">Backlog</option>
              <option value="TODO">To Do</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="REVIEW">In Review</option>
              <option value="BLOCKED">Blocked</option>
              <option value="COMPLETED">Completed</option>
            </select>

            <select
              className="filter-select"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              aria-label="Filter by priority"
            >
              <option value="all">All Priorities</option>
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
              <option value="URGENT">Urgent ⚡</option>
            </select>

            <button
              onClick={fetchProjectData}
              className="btn-secondary"
              style={{ height: '38px', width: '38px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', flexShrink: 0 }}
              title="Refresh project tasks"
            >
              <RefreshCw size={15} />
            </button>
          </div>

          {/* Mobile Filter Bar (<= 640px) - Single Row Icon-Only */}
          <div className="project-mobile-filter-bar tasks-mobile-filter-bar">
            {/* Search Toggle Icon */}
            <button
              onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              className={`mobile-filter-icon-btn ${mobileSearchOpen || searchQuery ? 'active' : ''}`}
              title="Search tasks"
              aria-label="Toggle search input"
            >
              <Search size={16} />
            </button>

            {/* Status Filter Icon with native Select overlay */}
            <div
              className={`mobile-filter-select-wrapper ${statusFilter !== 'all' ? 'active' : ''}`}
              title={`Status: ${statusFilter}`}
            >
              <CircleDot size={16} />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="all">All Statuses</option>
                <option value="BACKLOG">Backlog</option>
                <option value="TODO">To Do</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="REVIEW">In Review</option>
                <option value="BLOCKED">Blocked</option>
                <option value="COMPLETED">Completed</option>
              </select>
            </div>

            {/* Priority Filter Icon with native Select overlay */}
            <div
              className={`mobile-filter-select-wrapper ${priorityFilter !== 'all' ? 'active' : ''}`}
              title={`Priority: ${priorityFilter}`}
            >
              <Flag size={16} />
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                aria-label="Filter by priority"
              >
                <option value="all">All Priorities</option>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent ⚡</option>
              </select>
            </div>

            {/* Reset Filters Icon Button (Shown when active) */}
            {(statusFilter !== 'all' || priorityFilter !== 'all' || searchQuery) && (
              <button
                onClick={() => {
                  setStatusFilter('all');
                  setPriorityFilter('all');
                  setSearchQuery('');
                  setMobileSearchOpen(false);
                }}
                className="mobile-filter-icon-btn reset-btn"
                title="Reset all filters"
                aria-label="Reset filters"
              >
                <X size={15} />
              </button>
            )}

            {/* Refresh Icon Button */}
            <button
              onClick={fetchProjectData}
              className="mobile-filter-icon-btn"
              title="Refresh tasks"
              aria-label="Refresh tasks"
            >
              <RefreshCw size={15} />
            </button>
          </div>
        </div>

        {/* Expandable Mobile Search Bar */}
        {mobileSearchOpen && (
          <div className="tasks-mobile-search-expand">
            <Search size={15} color="var(--text-secondary)" style={{ flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Search in project..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="clear-search-btn"
                aria-label="Clear search query"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {/* Workspace View Contents */}
        {activeTab === 'list' && (
          <div className="tasks-table-container">
            <table className="tasks-table">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'rgba(255,255,255,0.02)', color: 'var(--text-secondary)', fontSize: '12px' }}>
                  <th style={{ padding: '12px 16px', width: '36px' }}></th>
                  <th style={{ padding: '12px 16px' }}>Task</th>
                  <th style={{ padding: '12px 16px' }}>Status</th>
                  <th style={{ padding: '12px 16px' }}>Priority</th>
                  <th style={{ padding: '12px 16px' }}>Subtasks</th>
                  <th style={{ padding: '12px 16px' }}>Due Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                      No tasks in this project yet. Click "+ Add Task" to create one.
                    </td>
                  </tr>
                ) : (
                  filteredTasks.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => setDetailDrawerTaskId(t.id)}
                      style={{ borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={{ padding: '12px 16px' }}>
                        <Star size={16} fill={t.is_favorite ? '#eab308' : 'none'} color={t.is_favorite ? '#eab308' : 'var(--text-secondary)'} />
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {t.title}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span
                          style={{
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: t.status === 'COMPLETED' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(59, 130, 246, 0.15)',
                            color: t.status === 'COMPLETED' ? 'var(--success)' : 'var(--accent-primary)',
                          }}
                        >
                          {t.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '11px', fontWeight: 600, color: t.priority === 'URGENT' ? 'var(--danger)' : 'var(--text-secondary)' }}>
                        {t.priority}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {t.subtasks_total > 0 ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100px' }}>
                            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{t.progress}%</span>
                            <div style={{ flex: 1, height: '4px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${t.progress}%`, background: 'var(--accent-primary)' }} />
                            </div>
                          </div>
                        ) : (
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {t.end_at ? new Date(t.end_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'board' && (
          <TaskKanbanBoard
            tasks={filteredTasks}
            onTaskClick={(t) => setDetailDrawerTaskId(t.id)}
            onStatusChange={handleStatusChange}
            onQuickAdd={(status) => setCreateTaskModalOpen(true)}
          />
        )}

        {activeTab === 'calendar' && (
          <TaskCalendarView
            tasks={filteredTasks}
            onTaskClick={(t) => setDetailDrawerTaskId(t.id)}
          />
        )}

        {activeTab === 'timeline' && (
          <TaskTimelineView
            tasks={filteredTasks}
            onTaskClick={(t) => setDetailDrawerTaskId(t.id)}
          />
        )}
      </div>

      <TaskCreateModal
        isOpen={createTaskModalOpen}
        onClose={() => setCreateTaskModalOpen(false)}
        onCreated={() => fetchProjectData()}
        initialProjectId={projectId}
        projects={project ? [project] : []}
      />

      <TaskDetailDrawer
        taskId={detailDrawerTaskId}
        isOpen={Boolean(detailDrawerTaskId)}
        onClose={() => setDetailDrawerTaskId(null)}
        onTaskUpdated={() => fetchProjectData()}
        allProjectTasks={tasks}
      />

      <ProjectMembersDrawer
        projectId={projectId}
        isOpen={membersDrawerOpen}
        onClose={() => setMembersDrawerOpen(false)}
        onMembersUpdated={() => fetchProjectData()}
      />
    </MainLayout>
  );
}
